import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { PlatformStoresService } from '../api/platform/services/platform-stores.service';

const CSV_CATALOG_MAX_SIZE_MB = 50;

function uploadFileWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  maxBytes: number,
  onProgress: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Content-Type', contentType);
    request.setRequestHeader('x-goog-content-length-range', `0,${maxBytes}`);

    request.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) {
        return;
      }
      onProgress(Math.round((event.loaded / event.total) * 100));
    });

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${request.status}`));
    });

    request.addEventListener('error', () => reject(new Error('Upload failed')));
    request.send(file);
  });
}

@Injectable({ providedIn: 'root' })
export class StoreCatalogUploadService {
  constructor(private readonly platformStoresService: PlatformStoresService) {}

  getCatalogContentType(file: File): 'text/csv' | 'text/tab-separated-values' {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return extension === 'tsv' ? 'text/tab-separated-values' : 'text/csv';
  }

  async uploadCatalog(
    storeId: string,
    file: File,
    onProgress: (progress: number) => void
  ): Promise<string> {
    const validationError = this.validateCatalogFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const contentType = this.getCatalogContentType(file);
    const { uploadUrl, catalogUrl } = await firstValueFrom(
      this.platformStoresService.storeCatalogsControllerCreateUploadUrl({
        body: {
          contentType,
          fileName: file.name,
        },
        storeId,
      })
    );

    await uploadFileWithProgress(
      uploadUrl,
      file,
      contentType,
      CSV_CATALOG_MAX_SIZE_MB * 1024 * 1024,
      onProgress
    );
    return catalogUrl;
  }

  validateCatalogFile(file: File): string | null {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (extension !== 'csv' && extension !== 'tsv') {
      return 'Only CSV and TSV files are allowed.';
    }

    if (file.size > CSV_CATALOG_MAX_SIZE_MB * 1024 * 1024) {
      return `File is too big. Max allowed file size is ${CSV_CATALOG_MAX_SIZE_MB}MB.`;
    }

    return null;
  }
}
